import { supabase } from './supabase';

function checkError(result: { error: any }, operation: string) {
  if (result.error) {
    console.error(`[beryl Cloud] ${operation} failed:`, result.error.message);
    throw new Error(`${operation}: ${result.error.message}`);
  }
}

export const SupabaseDataService = {
  async loadAllData(userId: string) {
    const queries = [
      supabase.from('pain_logs').select('*').eq('user_id', userId).order('timestamp', { ascending: false }),
      supabase.from('prescribed_exercises').select('*').eq('user_id', userId).order('name'),
      supabase.from('exercise_completions').select('*').eq('user_id', userId).order('date', { ascending: false }),
      supabase.from('meals').select('*').eq('user_id', userId).order('timestamp', { ascending: false }),
      supabase.from('documents').select('*').eq('user_id', userId).order('timestamp', { ascending: false }),
      supabase.from('appointments').select('*').eq('user_id', userId).order('date'),
      supabase.from('medications').select('*').eq('user_id', userId),
      supabase.from('supplements').select('*').eq('user_id', userId),
      supabase.from('supplement_logs').select('*').eq('user_id', userId).order('date', { ascending: false }),
      supabase.from('user_settings').select('*').eq('user_id', userId),
      supabase.from('user_notes').select('*').eq('user_id', userId).order('timestamp', { ascending: false }),
    ];
    const results = await Promise.all(queries);

    const tableNames = ['pain_logs', 'prescribed_exercises', 'exercise_completions', 'meals', 'documents', 'appointments', 'medications', 'supplements', 'supplement_logs', 'user_settings', 'user_notes'];
    for (let i = 0; i < results.length; i++) {
      if (results[i].error) {
        console.error(`[beryl Cloud] Failed to load ${tableNames[i]}:`, results[i].error?.message);
      }
    }

    return {
      painLogs: results[0].data || [],
      prescribedExercises: results[1].data || [],
      exerciseCompletions: results[2].data || [],
      meals: results[3].data || [],
      documents: results[4].data || [],
      appointments: results[5].data || [],
      medications: results[6].data || [],
      supplements: results[7].data || [],
      supplementLogs: results[8].data || [],
      settings: results[9].data || [],
      userNotes: results[10].data || [],
    };
  },

  async insertPainLog(userId: string, log: { id: string; level: number; location: string; notes: string; timestamp: string }) {
    const result = await supabase.from('pain_logs').insert({ ...log, user_id: userId });
    checkError(result, 'Insert pain log');
  },

  async updatePainLog(log: { id: string; level: number; location: string; notes: string; timestamp: string }) {
    const result = await supabase.from('pain_logs').update({ level: log.level, location: log.location, notes: log.notes, timestamp: log.timestamp }).eq('id', log.id);
    checkError(result, 'Update pain log');
  },

  async deletePainLog(id: string) {
    const result = await supabase.from('pain_logs').delete().eq('id', id);
    checkError(result, 'Delete pain log');
  },

  async insertPrescribedExercise(userId: string, ex: { id: string; name: string; sets: number; reps: number; duration: number; schedule_days: string; exercise_type: string; notes: string; weight: number }) {
    const result = await supabase.from('prescribed_exercises').insert({ ...ex, user_id: userId });
    checkError(result, 'Insert exercise');
  },

  async updatePrescribedExercise(ex: { id: string; name: string; sets: number; reps: number; duration: number; schedule_days: string; exercise_type: string; notes: string; weight: number }) {
    const result = await supabase.from('prescribed_exercises').update({ name: ex.name, sets: ex.sets, reps: ex.reps, duration: ex.duration, schedule_days: ex.schedule_days, exercise_type: ex.exercise_type, notes: ex.notes, weight: ex.weight }).eq('id', ex.id);
    checkError(result, 'Update exercise');
  },

  async deletePrescribedExercise(id: string) {
    await supabase.from('exercise_completions').delete().eq('exercise_id', id);
    const result = await supabase.from('prescribed_exercises').delete().eq('id', id);
    checkError(result, 'Delete exercise');
  },

  async toggleExerciseCompletion(userId: string, exerciseId: string, date: string, notes?: string) {
    const { data: existing } = await supabase.from('exercise_completions').select('id').eq('exercise_id', exerciseId).eq('date', date).single();
    if (existing) {
      const result = await supabase.from('exercise_completions').delete().eq('id', existing.id);
      checkError(result, 'Remove completion');
    } else {
      const id = Math.random().toString(36).substr(2, 9);
      const result = await supabase.from('exercise_completions').insert({ id, exercise_id: exerciseId, date, user_id: userId, notes: notes || '' });
      checkError(result, 'Add completion');
    }
  },

  async updateExerciseCompletionNotes(exerciseId: string, date: string, notes: string) {
    const result = await supabase.from('exercise_completions').update({ notes }).eq('exercise_id', exerciseId).eq('date', date);
    checkError(result, 'Update completion notes');
  },

  async insertMeal(userId: string, meal: { id: string; name: string; meal_type: string; protein: number; carbs: number; fats: number; calories: number; timestamp: string }) {
    const result = await supabase.from('meals').insert({ ...meal, user_id: userId });
    checkError(result, 'Insert meal');
  },

  async updateMeal(meal: { id: string; name: string; meal_type: string; protein: number; carbs: number; fats: number; calories: number; timestamp: string }) {
    const result = await supabase.from('meals').update({ name: meal.name, meal_type: meal.meal_type, protein: meal.protein, carbs: meal.carbs, fats: meal.fats, calories: meal.calories, timestamp: meal.timestamp }).eq('id', meal.id);
    checkError(result, 'Update meal');
  },

  async deleteMeal(id: string) {
    const result = await supabase.from('meals').delete().eq('id', id);
    checkError(result, 'Delete meal');
  },

  async insertAppointment(userId: string, apt: { id: string; title: string; date: string; location: string; notes: string }) {
    const result = await supabase.from('appointments').insert({ ...apt, user_id: userId });
    checkError(result, 'Insert appointment');
  },

  async deleteAppointment(id: string) {
    const result = await supabase.from('appointments').delete().eq('id', id);
    checkError(result, 'Delete appointment');
  },

  async insertSupplement(userId: string, supp: { id: string; name: string; dosage: string; icon: string; color: string }) {
    const result = await supabase.from('supplements').insert({ ...supp, user_id: userId });
    checkError(result, 'Insert supplement');
  },

  async deleteSupplement(id: string) {
    await supabase.from('supplement_logs').delete().eq('supplement_id', id);
    const result = await supabase.from('supplements').delete().eq('id', id);
    checkError(result, 'Delete supplement');
  },

  async toggleSupplementLog(userId: string, supplementId: string, date: string) {
    const { data: existing } = await supabase.from('supplement_logs').select('id').eq('supplement_id', supplementId).eq('date', date).single();
    if (existing) {
      const result = await supabase.from('supplement_logs').delete().eq('id', existing.id);
      checkError(result, 'Remove supplement log');
    } else {
      const result = await supabase.from('supplement_logs').insert({ supplement_id: supplementId, date, taken: true, user_id: userId });
      checkError(result, 'Add supplement log');
    }
  },

  async insertDocument(userId: string, doc: { id: string; name: string; type: string; uri: string; notes: string; timestamp: string }) {
    const result = await supabase.from('documents').insert({ ...doc, user_id: userId });
    checkError(result, 'Insert document');
  },

  async deleteDocument(id: string) {
    const result = await supabase.from('documents').delete().eq('id', id);
    checkError(result, 'Delete document');
  },

  async insertNote(userId: string, note: { id: string; title: string; content: string; category: string; timestamp: string }) {
    const result = await supabase.from('user_notes').insert({ ...note, user_id: userId });
    if (result.error) console.warn('[beryl] Insert note failed (table may not exist yet):', result.error.message);
  },

  async updateNote(note: { id: string; title: string; content: string; category: string; timestamp: string }) {
    const result = await supabase.from('user_notes').update({ title: note.title, content: note.content, category: note.category, timestamp: note.timestamp }).eq('id', note.id);
    if (result.error) console.warn('[beryl] Update note failed:', result.error.message);
  },

  async deleteNote(id: string) {
    const result = await supabase.from('user_notes').delete().eq('id', id);
    if (result.error) console.warn('[beryl] Delete note failed:', result.error.message);
  },

  async setSetting(userId: string, key: string, value: string) {
    const { data: existing } = await supabase.from('user_settings').select('id').eq('user_id', userId).eq('key', key).single();
    if (existing) {
      const result = await supabase.from('user_settings').update({ value }).eq('id', existing.id);
      checkError(result, 'Update setting');
    } else {
      const result = await supabase.from('user_settings').insert({ user_id: userId, key, value });
      checkError(result, 'Insert setting');
    }
  },
};
